import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, Eye, EyeOff, Mail, Lock, ArrowRight, ArrowLeft, ShieldCheck, CheckCircle2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { AuthShell, AuthCard, BrandIcon } from "@/components/AuthShell";
import { toast } from "sonner";

export default function ForgotPassword() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ email: "", otp: "", pw: "", pw2: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const timer = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => () => clearInterval(timer.current), []);
  const startCooldown = (s = 45) => {
    setCooldown(s);
    clearInterval(timer.current);
    timer.current = setInterval(() => setCooldown((c) => { if (c <= 1) { clearInterval(timer.current); return 0; } return c - 1; }), 1000);
  };

  const sendCode = async () => {
    if (!form.email || !form.email.includes("@")) return setErr("Please enter a valid email address.");
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email: form.email });
      toast.success(data.message || "If an account exists, we've sent a reset code.");
      setStep(2); startCooldown(45);
    } catch (e) { setErr(apiError(e)); } finally { setBusy(false); }
  };

  const verify = async () => {
    if (form.otp.length < 6) return setErr("Enter the 6-digit reset code.");
    setBusy(true); setErr("");
    try {
      await api.post("/auth/verify-reset", { email: form.email, otp: form.otp });
      toast.success("Code verified");
      setStep(3);
    } catch (e) { setErr(apiError(e)); } finally { setBusy(false); }
  };

  const reset = async () => {
    if (form.pw.length < 6) return setErr("Password must be at least 6 characters.");
    if (form.pw !== form.pw2) return setErr("Passwords do not match.");
    setBusy(true); setErr("");
    try {
      await api.post("/auth/reset-password", { email: form.email, new_password: form.pw });
      toast.success("Password reset successful. Please log in.");
      nav("/login");
    } catch (e) { setErr(apiError(e)); setBusy(false); }
  };

  return (
    <AuthShell>
      <AuthCard>
        <div className="flex flex-col items-center text-center mb-6">
          <BrandIcon className="h-16 w-16" />
          <h1 className="font-serif text-2xl font-bold text-[#58111A] mt-4" data-testid="forgot-title">
            {step === 1 ? "Reset your password" : step === 2 ? "Verify reset code" : "Create new password"}
          </h1>
          <p className="text-sm text-[#605255] mt-1">
            {step === 1 ? "Enter your email and we'll send a reset code." : step === 2 ? `We sent a 6-digit code to ${form.email}.` : "Choose a strong new password."}
          </p>
        </div>

        <div className="flex items-center gap-2 mb-5">
          {[1, 2, 3].map((s) => <div key={s} className={`h-1.5 flex-1 rounded-full transition-colors ${step >= s ? "bg-[#C59B27]" : "bg-[#EADFCF]"}`} />)}
        </div>

        {err && <div className="mb-4 text-sm text-[#991B1B] bg-[#FEF2F2] border border-[#FECACA] rounded-xl px-4 py-2.5" data-testid="forgot-error">{err}</div>}

        {step === 1 && (
          <div className="animate-fadeup">
            <label className="mj-label" htmlFor="fp-email">Email Address</label>
            <div className="relative">
              <Mail className="w-4 h-4 text-[#B99CA0] absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input id="fp-email" data-testid="forgot-email" type="email" className="mj-input pl-10" placeholder="Enter your email address" value={form.email} onChange={(e) => set("email", e.target.value.trim())} onKeyDown={(e) => e.key === "Enter" && sendCode()} />
            </div>
            <button data-testid="forgot-send" onClick={sendCode} disabled={busy} className="btn-primary w-full mt-5 py-3 disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send Reset Code <ArrowRight className="w-4 h-4" /></>}
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="animate-fadeup">
            <label className="mj-label" htmlFor="fp-otp">Reset Code</label>
            <div className="relative">
              <ShieldCheck className="w-4 h-4 text-[#B99CA0] absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input id="fp-otp" data-testid="forgot-otp" className="mj-input pl-10 tracking-[0.4em] text-lg text-center" placeholder="______" value={form.otp} onChange={(e) => set("otp", e.target.value.replace(/\D/g, "").slice(0, 6))} onKeyDown={(e) => e.key === "Enter" && verify()} />
            </div>
            <button data-testid="forgot-verify" onClick={verify} disabled={busy} className="btn-primary w-full mt-5 py-3 disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify Code"}
            </button>
            <div className="flex items-center justify-between mt-4">
              <button onClick={() => { setStep(1); setErr(""); }} className="text-sm text-[#605255] flex items-center gap-1"><ArrowLeft className="w-3.5 h-3.5" /> Change email</button>
              <button onClick={sendCode} disabled={busy || cooldown > 0} className="text-sm font-semibold text-[#781826] disabled:text-[#B99CA0]" data-testid="forgot-resend">
                {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="animate-fadeup">
            <div className="mb-4 flex items-center gap-2 text-sm text-[#15803D] bg-[#F0FDF4] border border-[#BBF7D0] rounded-xl px-4 py-2.5">
              <CheckCircle2 className="w-4 h-4" /> Code verified · {form.email}
            </div>
            <label className="mj-label" htmlFor="fp-pw">New Password</label>
            <div className="relative">
              <Lock className="w-4 h-4 text-[#B99CA0] absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input id="fp-pw" data-testid="forgot-pw" type={showPw ? "text" : "password"} className="mj-input pl-10 pr-11" placeholder="At least 6 characters" value={form.pw} onChange={(e) => set("pw", e.target.value)} />
              <button type="button" onClick={() => setShowPw((s) => !s)} aria-label={showPw ? "Hide password" : "Show password"} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9E7B1B] hover:text-[#781826]">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <label className="mj-label mt-3" htmlFor="fp-pw2">Confirm New Password</label>
            <div className="relative">
              <Lock className="w-4 h-4 text-[#B99CA0] absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input id="fp-pw2" data-testid="forgot-pw2" type={showPw ? "text" : "password"} className="mj-input pl-10" placeholder="Re-enter new password" value={form.pw2} onChange={(e) => set("pw2", e.target.value)} onKeyDown={(e) => e.key === "Enter" && reset()} />
            </div>
            <button data-testid="forgot-reset" onClick={reset} disabled={busy} className="btn-primary w-full mt-5 py-3 disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reset Password"}
            </button>
          </div>
        )}

        <p className="text-center text-sm text-[#605255] mt-6">Remembered it? <Link to="/login" className="text-[#781826] font-semibold">Back to Login</Link></p>
      </AuthCard>
      <p className="lg:hidden text-center text-[11px] text-[#9E7B1B] mt-6">Powered by Vijay Software Solutions Pvt Ltd</p>
    </AuthShell>
  );
}
